import { priceIconUrl } from "@/functions/getPriceDisplay";
import { t } from "@repo/shared/i18n";
import { EventToken, ObjectiveDisplay, PriceInfo } from "@repo/shared/types";
import { getCardPriceDisplay } from "@repo/shared/utils";
import { useMemo } from "react";

/**
 * What a price row shows, shared by every Price presentation: the
 * season-objective SP (icon + required amount) when the player is an
 * objective reward, otherwise the market price display. A tradeable market
 * price always wins over the objective SP. Decided in `@repo/shared/utils`
 * (`getCardPriceDisplay`), so the app shows the same.
 */
export const usePriceDisplay = ({
  priceInfo,
  objectiveDisplay,
  eventTokens,
  assetsBaseUrl,
}: {
  priceInfo?: PriceInfo;
  objectiveDisplay?: ObjectiveDisplay;
  eventTokens?: EventToken[];
  assetsBaseUrl: string;
}) =>
  useMemo(() => {
    const display = getCardPriceDisplay({
      priceInfo,
      objectiveDisplay,
      eventTokens,
    });
    // A token missing from the catalog: no icon, so the generic name stands in.
    const unknownToken =
      display.icon === null && priceInfo?.source === "tokens";
    return {
      iconUrl: priceIconUrl(display.icon, display.tokenIconKey, assetsBaseUrl),
      label: unknownToken
        ? t("players:detailed.currentState.priceSource.tokens")
        : display.label,
      price: display.price,
      showPrice: display.showPrice,
    };
  }, [priceInfo, objectiveDisplay, eventTokens, assetsBaseUrl]);
